import type { DragEndEvent, DragMoveEvent, DragStartEvent } from '@dnd-kit/core'
import { useSensor, useSensors } from '@dnd-kit/core'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { TripStop } from 'tripPlanner/api/types'
import {
  POINTER_DRAG_ACTIVATION_DISTANCE,
  TOUCH_DRAG_ACTIVATION_DELAY,
  TOUCH_DRAG_ACTIVATION_TOLERANCE,
  TimelinePointerSensor,
  TimelineTouchSensor,
  type PointerCoordinates,
  type TimelineDropPlacement,
  type TimelineDropTarget,
  type TimelineItem,
  type TimelineMove,
  applyOptimisticTimelineMove,
  buildTimelineItemById,
  buildTimelineItemLocationById,
  buildTimelineMove,
  canDragTimelineItem,
  getTimelineDropTargetFromPoint,
  isSameTimelineDropTarget,
} from './tripPlanCanvasReorder'
import { useDragPointerTracking } from './useDragPointerTracking'

interface UseTripPlanCanvasDragReorderParams {
  stops: Array<TripStop>
  disabled: boolean
  onMoveItem: (itemId: number, move: TimelineMove) => void | boolean | Promise<void | boolean>
}

interface UseTripPlanCanvasDragReorderResult {
  /** Stops with any in-flight optimistic move applied; render from this. */
  orderedStops: Array<TripStop>
  activeItem: TimelineItem | undefined
  overItemId: number | undefined
  dropPlacement: TimelineDropPlacement | undefined
  overDayId: number | undefined
  overUnplannedStopId: number | undefined
  isDragActive: boolean
  sensors: ReturnType<typeof useSensors>
  handleDragStart: (event: DragStartEvent) => void
  handleDragMove: (event: DragMoveEvent) => void
  handleDragEnd: (event: DragEndEvent) => void
  handleDragCancel: () => void
}

export function useTripPlanCanvasDragReorder({
  stops,
  disabled,
  onMoveItem,
}: UseTripPlanCanvasDragReorderParams): UseTripPlanCanvasDragReorderResult {
  const [activeItemId, setActiveItemId] = useState<number | undefined>(undefined)
  const [overItemId, setOverItemId] = useState<number | undefined>(undefined)
  const [dropPlacement, setDropPlacement] = useState<TimelineDropPlacement | undefined>(undefined)
  const [overDayId, setOverDayId] = useState<number | undefined>(undefined)
  const [overUnplannedStopId, setOverUnplannedStopId] = useState<number | undefined>(undefined)
  const [isDragActive, setIsDragActive] = useState(false)
  const [optimisticStops, setOptimisticStops] = useState<Array<TripStop> | undefined>(undefined)

  const activeItemIdRef = useRef<number | undefined>(undefined)
  const sourceDayIdRef = useRef<number | undefined>(undefined)
  const dropTargetRef = useRef<TimelineDropTarget | undefined>(undefined)
  const moveSequenceRef = useRef(0)

  const itemById = useMemo(() => buildTimelineItemById(stops), [stops])
  const itemLocationById = useMemo(() => buildTimelineItemLocationById(stops), [stops])

  const sensors = useSensors(
    useSensor(TimelinePointerSensor, {
      activationConstraint: {
        distance: POINTER_DRAG_ACTIVATION_DISTANCE,
      },
    }),
    useSensor(TimelineTouchSensor, {
      activationConstraint: {
        delay: TOUCH_DRAG_ACTIVATION_DELAY,
        tolerance: TOUCH_DRAG_ACTIVATION_TOLERANCE,
      },
    }),
  )

  useEffect(() => {
    setOptimisticStops(undefined)
  }, [stops])

  const resolveDropTarget = useCallback((dropPoint: PointerCoordinates): TimelineDropTarget | undefined => {
    const target = getTimelineDropTargetFromPoint(dropPoint, activeItemIdRef.current)
    // Appending to the day the item already lives in isn't a meaningful move, so
    // it isn't a valid target (a specific-item drop within that day still is).
    if (target?.type === 'day' && target.dayId === sourceDayIdRef.current) return undefined
    return target
  }, [])

  const updateDropTargetFromPoint = useCallback((dropPoint: PointerCoordinates) => {
    const nextTarget = resolveDropTarget(dropPoint)
    if (isSameTimelineDropTarget(nextTarget, dropTargetRef.current)) return

    dropTargetRef.current = nextTarget
    setOverItemId(nextTarget?.type === 'item' ? nextTarget.itemId : undefined)
    setDropPlacement(nextTarget?.type === 'item' ? nextTarget.placement : undefined)
    setOverDayId(nextTarget?.type === 'day' ? nextTarget.dayId : undefined)
    setOverUnplannedStopId(nextTarget?.type === 'unplanned' ? nextTarget.stopId : undefined)
  }, [resolveDropTarget])

  const { startTracking, stopTracking, getDropPoint } = useDragPointerTracking({
    isDragActive,
    onDropPointChange: updateDropTargetFromPoint,
  })

  const resetDragState = useCallback(() => {
    setActiveItemId(undefined)
    setOverItemId(undefined)
    setDropPlacement(undefined)
    setOverDayId(undefined)
    setOverUnplannedStopId(undefined)
    setIsDragActive(false)
    activeItemIdRef.current = undefined
    sourceDayIdRef.current = undefined
    dropTargetRef.current = undefined
    stopTracking()
  }, [stopTracking])

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const itemId = Number(event.active.id)
    const item = Number.isFinite(itemId) ? itemById.get(itemId) : undefined
    if (!item || disabled || !canDragTimelineItem(item)) {
      resetDragState()
      return
    }

    setOverItemId(undefined)
    setDropPlacement(undefined)
    setOverDayId(undefined)
    setOverUnplannedStopId(undefined)
    setIsDragActive(true)
    setActiveItemId(itemId)
    activeItemIdRef.current = itemId
    sourceDayIdRef.current = itemLocationById.get(itemId)?.dayId
    dropTargetRef.current = undefined
    startTracking(event)
  }, [disabled, itemById, itemLocationById, resetDragState, startTracking])

  const handleDragMove = useCallback((event: DragMoveEvent) => {
    const dropPoint = getDropPoint(event)
    if (!dropPoint) return
    updateDropTargetFromPoint(dropPoint)
  }, [getDropPoint, updateDropTargetFromPoint])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const dropPoint = getDropPoint(event)
    const itemId = activeItemIdRef.current
    const sourceDayId = sourceDayIdRef.current
    const target = dropPoint ? getTimelineDropTargetFromPoint(dropPoint, itemId) : undefined
    resetDragState()

    if (disabled || itemId === undefined || !target) return

    const item = itemById.get(itemId)
    if (!item || !canDragTimelineItem(item)) return

    const move = buildTimelineMove(itemId, target, sourceDayId, itemLocationById)
    if (!move) return

    // Optimistically relocate the item so the drop lands where the insertion line
    // showed, then roll back if the move fails and no newer drag has superseded it.
    // Build on any in-flight optimistic order (not just canonical stops) so a second
    // drop before the first refetch lands keeps the earlier move visible.
    const previousOptimisticStops = optimisticStops
    setOptimisticStops(prev => applyOptimisticTimelineMove(prev ?? stops, itemId, target))
    const moveToken = ++moveSequenceRef.current
    const rollback = () => {
      if (moveSequenceRef.current !== moveToken) return
      setOptimisticStops(previousOptimisticStops)
    }

    Promise.resolve(onMoveItem(itemId, move)).then(moved => {
      if (moved === false) rollback()
    }).catch(rollback)
  }, [disabled, getDropPoint, itemById, itemLocationById, onMoveItem, optimisticStops, resetDragState, stops])

  const handleDragCancel = useCallback(() => {
    resetDragState()
  }, [resetDragState])

  const activeItem = activeItemId !== undefined ? itemById.get(activeItemId) : undefined

  return {
    orderedStops: optimisticStops ?? stops,
    activeItem,
    overItemId,
    dropPlacement,
    overDayId,
    overUnplannedStopId,
    isDragActive,
    sensors,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    handleDragCancel,
  }
}
